import { isDefined } from "@clipboard-health/util-ts";
import { PROCESS_MODAL_STATE, ROLE_MAPPINGS } from "src/containers/teamMembers/constants";
import { QuickActions } from "src/containers/teamMembers/QuickAction";
import { EmailText, EmailWrapper, NumberPrefix, Row } from "src/containers/teamMembers/styles";
import { TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import { getHighestAccessRole } from "src/containers/teamMembers/utils";
import { Column, WorkerName } from "src/containers/workers/ListWorkers/ListWorkers.styles";
import { SessionType } from "src/modules/interface";
import { IUser } from "src/modules/myAccount/types";
import { formatPhone } from "src/utils/strings";
import { REBRAND_SECONDARY_COLOR } from "@src/appV2/lib/Theme/rebrandConstants";
import React, { Dispatch, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { useRestrictedSignedProfile } from "src/hooks/useRestrictedSignedProfile";
import { SuspiciousSwitch } from "./SuspiciousSwitch";
import { Button } from "antd";
import { refreshContact } from "@src/api/facilityUsers";
import { FacilityUserInternalNotesButton } from "@src/appV2/FacilityUser/InternalNotes/Button";
import { FacilityUserInternalNotesTruncatedDisplay } from "@src/appV2/FacilityUser/InternalNotes/TruncatedDisplay";
import { Box, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import {
  FACILITY_ROLES,
  FACILITY_USER_PERMISSION_LABELS,
  FACILITY_USER_PERMISSIONS,
  type FacilityUserPermission,
} from "src/constants/facility";
import useVisitRateSettingConfiguration from "@src/appV2/ExperimentalHomeHealth/Cases/api/useVisitRateSetting";

function getPermissionLabels(
  permissions: string[] | undefined,
  visitRateSettingEnabled: boolean,
  variableChargeEnabled: boolean
): string[] {
  if (!isDefined(permissions) || permissions.length === 0) {
    return [];
  }

  return permissions
    .filter(
      (permission) =>
        permission !== FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE || visitRateSettingEnabled
    )
    .filter(
      (permission) =>
        permission !== FACILITY_USER_PERMISSIONS.EDIT_SHIFT_RATE || variableChargeEnabled
    )
    .map((permission) => FACILITY_USER_PERMISSION_LABELS[permission as FacilityUserPermission])
    .filter((label): label is string => Boolean(label));
}

function getWorkerNameColor(isLoggedInUser: boolean): string {
  if (!isLoggedInUser) {
    return "inherit";
  }
  return REBRAND_SECONDARY_COLOR;
}

interface TeamMemberRowProps {
  member: IUser;
  canEditNotifications: boolean;
  dispatch: Dispatch<TeamMemberActions>;
}

export function TeamMemberRow(props: TeamMemberRowProps) {
  const { member, canEditNotifications, dispatch } = props;
  const { wrapRestrictedAction } = useRestrictedSignedProfile();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const { user: sessionUser, admin, profile } = useSelector((state: SessionType) => state.session);
  const workplaceId = profile.userId;
  const variableChargeEnabled = profile?.variableCharge?.enabled ?? false;

  const visitRateSettingConfiguration = useVisitRateSettingConfiguration();
  const isDesktop = useIsDesktop();

  const handleCopyEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (member.email) {
      void navigator.clipboard.writeText(member.email);
      setCopiedEmail(true);
      setTimeout(() => {
        setCopiedEmail(false);
      }, 2000);
    }
  };

  const toggleCredentialsModal = wrapRestrictedAction(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      dispatch({
        type: "SET_PROCESS_MODAL_VISIBILITY",
        processModal: PROCESS_MODAL_STATE.edit,
      });
      dispatch({
        type: "SET_MODAL_MEMBER",
        member,
      });
    },
    { triggeredBy: "edit account", triggeredFrom: "Team Members" }
  );

  const toggleNotificationsModal = wrapRestrictedAction(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      dispatch({
        type: "SET_NOTIFICATIONS_MODAL_VISIBILITY",
        notificationModal: true,
      });
      dispatch({
        type: "SET_MODAL_MEMBER",
        member,
      });
    },
    { triggeredBy: "edit notifications", triggeredFrom: "Team Members" }
  );

  const isLoggedInUser = member._id === sessionUser?._id;
  const path = isLoggedInUser
    ? getLocation("myAccount")
    : getLocation("workplaceUserDetails", {
        pathParams: { userId: member._id },
      });

  // Based on the highest role we allow the logged in user to edit other workplace members.
  // Edit account button is visible if the logged in user's highest access role is equal or
  // higher than the workplace member.
  const isHigherAccessRole =
    getHighestAccessRole(sessionUser?.access) >= getHighestAccessRole(member.roles);
  const highestAccessRole = getHighestAccessRole(member.roles, true);

  if (!isDesktop) {
    return (
      <Row isLoggedInUser={isLoggedInUser}>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
          <MobileField label="Member">
            <WorkerName
              style={{
                marginBottom: 0,
                color: getWorkerNameColor(isLoggedInUser),
              }}
            >
              {isLoggedInUser ? <Link to={path}>{member.name} (You)</Link> : member.name}
            </WorkerName>
            <EmailWrapper>
              <EmailText>{member.email}</EmailText>
              {member.email && (
                <Tooltip title={copiedEmail ? "Copied!" : "Copy email"}>
                  <IconButton
                    onClick={handleCopyEmail}
                    size="small"
                    sx={{ padding: 0, flexShrink: 0 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </EmailWrapper>
            <Box>
              <FacilityUserInternalNotesButton facilityUser={member} workplaceId={workplaceId} />
              <FacilityUserInternalNotesTruncatedDisplay
                facilityUser={member}
                workplaceId={workplaceId}
              />
            </Box>
          </MobileField>

          {(member?.phone || member?.workplacePhone) && (
            <MobileField label="Phone">
              {member?.phone && (
                <div>
                  <NumberPrefix>M:</NumberPrefix> {formatPhone(member.phone)}
                </div>
              )}
              {member?.workplacePhone && (
                <div>
                  <NumberPrefix>L:</NumberPrefix> {formatPhone(member.workplacePhone)}
                </div>
              )}
            </MobileField>
          )}

          <MobileField label="Role">
            <div>{ROLE_MAPPINGS[highestAccessRole]?.label}</div>
            {highestAccessRole !== FACILITY_ROLES.ADMIN &&
              getPermissionLabels(
                member.permissions,
                visitRateSettingConfiguration.enabled,
                variableChargeEnabled
              ).map((label) => (
                <Box
                  key={label}
                  sx={(theme) => ({ fontSize: "0.75rem", color: theme.palette.text.secondary })}
                >
                  {label}
                </Box>
              ))}
          </MobileField>

          {member?.designation && (
            <MobileField label="Job Title">
              <div>{member.designation}</div>
            </MobileField>
          )}

          {admin && (
            <MobileField label="Suspicious">
              <SuspiciousSwitch member={member} dispatch={dispatch} />
            </MobileField>
          )}

          {admin && (
            <MobileField label="Sync Knock">
              <Button
                type="primary"
                size="large"
                onClick={async () => {
                  await refreshContact(member._id);
                }}
              >
                Sync
              </Button>
            </MobileField>
          )}

          {(canEditNotifications || isLoggedInUser || isHigherAccessRole) && (
            <MobileField label="Actions">
              <QuickActions
                toggleCredentialsModal={toggleCredentialsModal}
                toggleNotificationsModal={toggleNotificationsModal}
                isLoggedInUser={isLoggedInUser}
                isHigherAccessRole={isHigherAccessRole}
                canEditNotifications={canEditNotifications}
              />
            </MobileField>
          )}
        </Box>
      </Row>
    );
  }

  return (
    <Row style={{ minHeight: 95 }} isLoggedInUser={isLoggedInUser}>
      <Column
        flex={1.25}
        style={{
          wordBreak: "break-word",
          overflow: "visible",
        }}
      >
        <WorkerName
          style={{
            marginBottom: 0,
            color: getWorkerNameColor(isLoggedInUser),
          }}
        >
          {isLoggedInUser ? <Link to={path}>{member.name} (You)</Link> : member.name}
        </WorkerName>
        <EmailWrapper>
          <EmailText>{member.email}</EmailText>
          {member.email && (
            <Tooltip title={copiedEmail ? "Copied!" : "Copy email"}>
              <IconButton
                onClick={handleCopyEmail}
                size="small"
                sx={{ padding: "2px", flexShrink: 0 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </EmailWrapper>
        <Box>
          <FacilityUserInternalNotesButton facilityUser={member} workplaceId={workplaceId} />
          <FacilityUserInternalNotesTruncatedDisplay
            facilityUser={member}
            workplaceId={workplaceId}
          />
        </Box>
      </Column>
      <Column>
        {member?.phone && (
          <div>
            <NumberPrefix>M:</NumberPrefix> {formatPhone(member?.phone)}
          </div>
        )}
        {member?.workplacePhone && (
          <div>
            <NumberPrefix>L:</NumberPrefix> {formatPhone(member?.workplacePhone)}
          </div>
        )}
      </Column>
      <Column>
        <div>{ROLE_MAPPINGS[highestAccessRole]?.label}</div>
        {highestAccessRole !== FACILITY_ROLES.ADMIN &&
          getPermissionLabels(
            member.permissions,
            visitRateSettingConfiguration.enabled,
            variableChargeEnabled
          ).map((label) => (
            <Box
              key={label}
              sx={(theme) => ({ fontSize: "0.75rem", color: theme.palette.text.secondary })}
            >
              {label}
            </Box>
          ))}
      </Column>
      <Column>{member?.designation && <div>{member.designation}</div>}</Column>
      {admin && (
        <Column>
          <SuspiciousSwitch member={member} dispatch={dispatch} />
        </Column>
      )}
      {admin && (
        <Column>
          <Button
            type="primary"
            size="large"
            onClick={async () => {
              await refreshContact(member._id);
            }}
          >
            Sync
          </Button>
        </Column>
      )}
      <Column>
        {(canEditNotifications || isLoggedInUser || isHigherAccessRole) && (
          <QuickActions
            toggleCredentialsModal={toggleCredentialsModal}
            toggleNotificationsModal={toggleNotificationsModal}
            isLoggedInUser={isLoggedInUser}
            isHigherAccessRole={isHigherAccessRole}
            canEditNotifications={canEditNotifications}
          />
        )}
      </Column>
    </Row>
  );
}

interface MobileFieldProps {
  label: string;
  children: React.ReactNode;
}

function MobileField(props: MobileFieldProps): React.ReactElement {
  const { label, children } = props;
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Box
        sx={(theme) => ({
          minWidth: 90,
          flexShrink: 0,
          color: theme.palette.text.secondary,
          fontSize: "0.6875rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        })}
      >
        {label}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
